import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ChevronDownIcon, SlidersHorizontalIcon } from 'lucide-react';
import { SlideFrame } from '../SlideFrame';
import { TalabAskBar } from '../TalabWindow';
import { useSequence } from '../../../hooks/useSequence';
import { useTypewriter } from '../../../hooks/useTypewriter';
const EASE = [0.23, 1, 0.32, 1] as const;
const FILTERS = ['Category', 'Price', 'Brand', 'Rating', 'Availability', 'Sort by'];
const MENU = ['New in', 'Best sellers', 'Offers', 'Collections'];
const ASK = 'I need a family SUV for next weekend under AED 400 a day.';

/**
 * Paced as five deliberate beats so a room can follow the argument:
 * 1 · headline alone (1.5s) — 2 · the browse era assembles itself (~3s)
 * 3 · it holds, then collapses (1.6s) — 4 · one input takes its place (1s)
 * 5 · the question types out, then rests. Roughly 12s end to end.
 */
export function SlideMarketShift({
  active
}: {
  active: boolean;
}) {
  const step = useSequence([1500, 3100, 1400, 1300, 1500], active);
  const siteVisible = step >= 1;
  const collapsing = step >= 3;
  const asking = step >= 4;
  const typing = step >= 5;
  const {
    typed,
    done
  } = useTypewriter(ASK, {
    active: typing,
    startDelay: 320,
    speed: 40
  });
  return <SlideFrame eyebrow="Market shift" title="Commerce is entering the answer era" lead="For years, websites were built around search bars, menus, filters, categories, and product pages. Customers now expect instant answers, conversational interactions, and personalized recommendations." footnote={<motion.div className="pt-5 border-t border-slate-100" animate={{
    opacity: collapsing ? 1 : 0
  }} transition={{
    duration: 0.9,
    ease: EASE
  }} data-id="element-2039" data-mp="2sw8b7cn3azg2">
          <p className="font-display font-bold text-[17px] text-dark-navy" data-id="element-2040" data-mp="32v7k8wbrox3l">
            The next evolution of commerce is not more filters.{' '}
            <span className="text-primary-indigo" data-id="element-2041" data-mp="37u0orz92tdrm">It is better answers.</span>
          </p>
        </motion.div>} data-id="element-2038" data-mp="2f53ikui537f8">
      <div className="relative h-full flex items-center justify-center" data-id="element-2042" data-mp="2i3ad8tdbmbo0">
        {/* Era labels */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-center gap-3" data-id="element-2043" data-mp="2d4phb653qe5d">
          <AnimatePresence mode="wait" data-id="element-2044" data-mp="2rx9gjvp4s1hj">
            <motion.span key={asking ? 'now' : 'then'} className="text-[9.5px] font-semibold uppercase tracking-[0.24em] text-slate-400" initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: siteVisible ? 1 : 0,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.6,
            ease: EASE
          }} data-id="element-2045" data-mp="2n4g74uipw4z4">
              {asking ? 'The answer era — one question' : 'The browse era — search, filter, repeat'}
            </motion.span>
          </AnimatePresence>
        </div>

        <div className="relative w-[760px] h-[268px] mt-8" data-id="element-2046" data-mp="1gi49yglvpisn">
          {/* Browse-era interface */}
          <motion.div className="absolute inset-0 flex flex-col gap-3.5" animate={{
          opacity: collapsing ? 0 : 1,
          scale: collapsing ? 0.88 : 1,
          filter: collapsing ? 'blur(6px)' : 'blur(0px)'
        }} transition={{
          duration: 1.6,
          ease: EASE
        }} data-id="element-2047" data-mp="1bjjaijegb359">
            <div className="flex items-center gap-3" data-id="element-2048" data-mp="3id1oxleq87pf">
              <motion.div className="flex-1 flex items-center gap-2 h-10 px-4 rounded-xl bg-white border border-slate-200 shadow-soft" initial={{
              opacity: 0,
              y: 10
            }} animate={siteVisible ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 0,
              y: 10
            }} transition={{
              duration: 0.7,
              ease: EASE
            }} data-id="element-2049" data-mp="3ibgvjz45eut5">
                <SearchIcon className="w-3.5 h-3.5 text-slate-300" data-id="element-2050" data-mp="2zfqqu44tzkbk" />
                <span className="text-[11.5px] text-slate-300" data-id="element-2051" data-mp="snzrizu6q0zy">Search products…</span>
              </motion.div>
              {MENU.map((item, index) => <motion.span key={item} className="flex items-center gap-1 text-[11px] text-slate-500" initial={{
              opacity: 0,
              y: 8
            }} animate={siteVisible ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 0,
              y: 8
            }} transition={{
              duration: 0.6,
              ease: EASE,
              delay: 0.55 + index * 0.18
            }} data-id="element-2052" data-mp="xli2evrriqal" data-mpi={index}>
                  {item}
                  <ChevronDownIcon className="w-3 h-3 text-slate-300" data-id="element-2053" data-mp="2ft2c3javques" />
                </motion.span>)}
            </div>

            <div className="flex items-center gap-2" data-id="element-2054" data-mp="260mpg8on1hnk">
              <motion.span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-100 text-[10.5px] text-slate-500" initial={{
              opacity: 0,
              scale: 0.96
            }} animate={siteVisible ? {
              opacity: 1,
              scale: 1
            } : {
              opacity: 0,
              scale: 0.96
            }} transition={{
              duration: 0.5,
              ease: EASE,
              delay: 1.3
            }} data-id="element-2055" data-mp="25z1w2me284ra">
                <SlidersHorizontalIcon className="w-3 h-3" data-id="element-2056" data-mp="1n2sl47ygfubi" /> Filters
              </motion.span>
              {FILTERS.map((filter, index) => <motion.span key={filter} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-[10.5px] text-slate-500" initial={{
              opacity: 0,
              scale: 0.96
            }} animate={siteVisible ? {
              opacity: 1,
              scale: 1
            } : {
              opacity: 0,
              scale: 0.96
            }} transition={{
              duration: 0.5,
              ease: EASE,
              delay: 1.42 + index * 0.13
            }} data-id="element-2057" data-mp="7j2s1lyhz7kj" data-mpi={index}>
                  {filter}
                  <ChevronDownIcon className="w-2.5 h-2.5 text-slate-300" data-id="element-2058" data-mp="3ozufaqunj4nv" />
                </motion.span>)}
            </div>

            <div className="grid grid-cols-6 gap-3 flex-1" data-id="element-2059" data-mp="3trktj7wpmckk">
              {Array.from({
              length: 6
            }).map((_, index) => <motion.div key={index} className="rounded-xl bg-white border border-slate-200 overflow-hidden flex flex-col" initial={{
              opacity: 0,
              y: 14
            }} animate={siteVisible ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 0,
              y: 14
            }} transition={{
              duration: 0.6,
              ease: EASE,
              delay: 2.2 + index * 0.12
            }} data-id="element-2060" data-mp="2x85154sej2pu" data-mpi={index}>
                  <div className="flex-1 bg-slate-100" data-id="element-2061" data-mp="i4rsgsnrdm1l" />
                  <div className="p-2 space-y-1.5" data-id="element-2062" data-mp="d684yntio274">
                    <div className="h-1.5 w-3/4 rounded-full bg-slate-200" data-id="element-2063" data-mp="d8kvvqh95mwb" />
                    <div className="h-1.5 w-1/2 rounded-full bg-slate-100" data-id="element-2064" data-mp="3lmthufp9rffi" />
                  </div>
                </motion.div>)}
            </div>
          </motion.div>

          {/* One conversational input takes its place */}
          <motion.div className="absolute inset-x-[110px] top-[86px]" initial={false} animate={{
          opacity: asking ? 1 : 0,
          scale: asking ? 1 : 0.96,
          y: asking ? 0 : 16
        }} transition={{
          duration: 1,
          ease: EASE
        }} data-id="element-2065" data-mp="3qlec7yhub4w1">
            <TalabAskBar text={typed} caret={typing} focused={asking} data-id="element-2066" data-mp="1deevgguhzrnc" />
            <motion.p className="mt-4 text-center text-[11.5px] text-slate-400" animate={{
            opacity: done ? 1 : 0
          }} transition={{
            duration: 0.7,
            ease: EASE,
            delay: done ? 0.35 : 0
          }} data-id="element-2067" data-mp="1idhsbe0bqrk8">
              No menus. No filters. Just intent.
            </motion.p>
          </motion.div>
        </div>
      </div>
    </SlideFrame>;
}